import * as React from "react"

import PlanCard from "@/components/PlanCard"
import { useTranslation, useI18next } from "@zestia/gatsby-plugin-react-i18next"
import { gtmSignupStart } from "@/utils/gtm-tracking"

const CAROUSEL_MAX_WIDTH_QUERY = "(max-width: 1199px)"
const SNAPSHOT_CAROUSEL_MAX_WIDTH_QUERY = "(max-width: 1023px)"
const THREE_COLUMN_MIN_GRID_WIDTH = 1098
const BASE_PLAN_TIERS = [
  { tier: "starter" },
  { tier: "growth", highlight: true },
  { tier: "advanced" },
]

const subscribeToMediaQuery = (queryList, handler) => {
  if (queryList.addEventListener) {
    queryList.addEventListener("change", handler)
    return () => queryList.removeEventListener("change", handler)
  }

  queryList.addListener(handler)
  return () => queryList.removeListener(handler)
}

/**
 * PlanColumns component
 * @param {boolean} snapshot - Whether to show the smaller snapshot version of the plan columns
 * @param {boolean} showFreeAsColumn - Whether to show the free plan as a column in the plan columns
 * @param {string} frequency - The frequency of the plan
 * @param {function} onSelectPlan - Function to call when a plan is selected
 * @param {function|string} onSeeAllFeaturesSelect - Function to call when the see all features button is selected or the URL to link to
 */
const PlanColumns = ({
  snapshot = false,
  showFreeAsColumn = false,
  showAllColumns = false,
  hideUltimateColumn = false,
  discount = 0,
  frequency,
  onSelectPlan,
  onSeeAllFeaturesSelect,
  className = "",
  aboveColumns,
  signupVariant = "",
  planButtonText,
}) => {
  const { t } = useTranslation()
  const { originalPath } = useI18next()
  const [isCarouselViewport, setIsCarouselViewport] = React.useState(false)
  const [isSnapshotCarouselViewport, setIsSnapshotCarouselViewport] =
    React.useState(false)
  const [columnsContainerWidth, setColumnsContainerWidth] = React.useState(null)
  const [isPointerDragging, setIsPointerDragging] = React.useState(false)
  const columnsRef = React.useRef(null)
  const dragStateRef = React.useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
  })
  const removeDragListenersRef = React.useRef(() => {})

  const shouldUseThreeColumnLayout = !showFreeAsColumn && hideUltimateColumn
  const canFitThreeColumns =
    columnsContainerWidth !== null &&
    columnsContainerWidth >= THREE_COLUMN_MIN_GRID_WIDTH
  const baseIsCarouselMode =
    isCarouselViewport && (!snapshot || isSnapshotCarouselViewport)
  const isCarouselMode = shouldUseThreeColumnLayout
    ? baseIsCarouselMode || !canFitThreeColumns
    : baseIsCarouselMode
  const shouldAutoScrollToSecondPlan =
    isCarouselMode &&
    !snapshot &&
    (showFreeAsColumn || shouldUseThreeColumnLayout)
  const shouldForceAllColumns = snapshot && showAllColumns
  const shouldRenderUltimate =
    shouldForceAllColumns ||
    (!hideUltimateColumn && (!showFreeAsColumn || isCarouselMode))
  const shouldRenderUltimateFooter =
    !shouldForceAllColumns && showFreeAsColumn && !snapshot && !isCarouselMode
  const useShortPlanDescriptions = originalPath === "/signup/" && isCarouselMode

  React.useEffect(() => {
    if (!shouldAutoScrollToSecondPlan || !columnsRef.current) return

    const columns = columnsRef.current
    const targetColumn = columns.querySelectorAll(".plan-columns__column")[1]
    if (!targetColumn) return

    const maxScrollLeft = Math.max(columns.scrollWidth - columns.clientWidth, 0)
    const centeredScrollLeft =
      targetColumn.offsetLeft -
      (columns.clientWidth - targetColumn.clientWidth) / 2
    const nextScrollLeft = Math.min(
      Math.max(centeredScrollLeft, 0),
      maxScrollLeft,
    )

    columns.scrollTo({ left: nextScrollLeft, behavior: "auto" })
  }, [shouldAutoScrollToSecondPlan])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined

    const carouselQuery = window.matchMedia(CAROUSEL_MAX_WIDTH_QUERY)
    const snapshotCarouselQuery = window.matchMedia(
      SNAPSHOT_CAROUSEL_MAX_WIDTH_QUERY,
    )

    const handleCarouselMediaChange = event => {
      setIsCarouselViewport(event.matches)
    }

    const handleSnapshotCarouselMediaChange = event => {
      setIsSnapshotCarouselViewport(event.matches)
    }

    setIsCarouselViewport(carouselQuery.matches)
    setIsSnapshotCarouselViewport(snapshotCarouselQuery.matches)

    const unsubscribeCarousel = subscribeToMediaQuery(
      carouselQuery,
      handleCarouselMediaChange,
    )
    const unsubscribeSnapshotCarousel = subscribeToMediaQuery(
      snapshotCarouselQuery,
      handleSnapshotCarouselMediaChange,
    )

    return () => {
      unsubscribeCarousel()
      unsubscribeSnapshotCarousel()
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === "undefined" || !columnsRef.current) return undefined

    const updateColumnsMeasurements = () => {
      if (!columnsRef.current) return

      const { clientWidth } = columnsRef.current
      setColumnsContainerWidth(clientWidth)
    }

    updateColumnsMeasurements()
    const resizeObserver = new window.ResizeObserver(updateColumnsMeasurements)
    resizeObserver.observe(columnsRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  React.useEffect(() => {
    return () => {
      removeDragListenersRef.current()
    }
  }, [])

  const planTierConfigs = React.useMemo(() => {
    const tiers = [...BASE_PLAN_TIERS]

    if (showFreeAsColumn) {
      tiers.unshift({ tier: "free" })
    }

    if (shouldRenderUltimate) {
      tiers.push({ tier: "ultimate" })
    }

    return tiers
  }, [showFreeAsColumn, shouldRenderUltimate])

  const shortAiBadge = planTierConfigs.length === 5

  const renderPlanColumn = (tier, options = {}) => (
    <div className="plan-columns__column" key={tier}>
      <PlanCard
        tier={tier}
        snapshot={snapshot}
        frequency={frequency}
        discount={discount}
        highlight={options.highlight}
        onSelect={() => onSelectPlan(tier)}
        onSeeAllFeaturesSelect={onSeeAllFeaturesSelect}
        signupVariant={signupVariant}
        planButtonText={planButtonText}
        useShortDescription={useShortPlanDescriptions}
        shortAiBadge={shortAiBadge}
      />
    </div>
  )

  const planColumns = (
    <>
      {planTierConfigs.map(({ tier, highlight }) =>
        renderPlanColumn(tier, { highlight }),
      )}
    </>
  )

  const handleCarouselPointerDown = event => {
    if (
      !isCarouselMode ||
      !columnsRef.current ||
      event.pointerType === "touch"
    ) {
      return
    }

    if (event.button !== 0) return

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: columnsRef.current.scrollLeft,
    }
    setIsPointerDragging(true)

    const handlePointerMove = moveEvent => {
      if (!dragStateRef.current.isDragging || !columnsRef.current) return

      const deltaX = moveEvent.clientX - dragStateRef.current.startX
      columnsRef.current.scrollLeft =
        dragStateRef.current.startScrollLeft - deltaX
    }

    const stopDragging = () => {
      dragStateRef.current.isDragging = false
      setIsPointerDragging(false)
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", stopDragging)
      window.removeEventListener("pointercancel", stopDragging)
      removeDragListenersRef.current = () => {}
    }

    removeDragListenersRef.current()
    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", stopDragging)
    window.addEventListener("pointercancel", stopDragging)
    removeDragListenersRef.current = stopDragging

    event.preventDefault()
  }

  const buildContainerClassName = React.useCallback(() => {
    const classes = [
      "plan-columns",
      !snapshot && !isCarouselMode && "container-fluid",
      !snapshot && "row",
      snapshot && "plan-columns--snapshot",
      shouldForceAllColumns && "plan-columns--snapshot-all-columns",
      shouldUseThreeColumnLayout && "plan-columns--three-column",
      isCarouselMode && "plan-columns--carousel-mode",
      isPointerDragging && "plan-columns--dragging",
      shouldUseThreeColumnLayout &&
        isCarouselMode &&
        "plan-columns--three-column-carousel",
      className,
    ]

    return classes.filter(Boolean).join(" ")
  }, [
    className,
    isCarouselMode,
    isPointerDragging,
    shouldForceAllColumns,
    shouldUseThreeColumnLayout,
    snapshot,
  ])

  return (
    <div className={buildContainerClassName()}>
      <section className="col-xs-12 mb-0">
        {aboveColumns && aboveColumns}

        <div className={`${aboveColumns ? "mt-8" : ""}`}>
          <div className="inner-anchor" id="pricing-tiers"></div>
          <div
            ref={columnsRef}
            className="mb-4 plan-columns__columns"
            onPointerDown={handleCarouselPointerDown}
          >
            {isCarouselMode ? (
              <div className="plan-columns__columns-track">{planColumns}</div>
            ) : (
              planColumns
            )}
          </div>

          {!showFreeAsColumn && !snapshot && (
            <div className="plan-columns__footer">
              <div className="col-xs-12 col-sm-8">
                <div className="plan-columns__footer-title">
                  <img
                    src={`/icons/signup/free.svg`}
                    alt=""
                    role="presentation"
                  />
                  <div className="plan-columns__footer-copy">
                    <h2 className="mb-0 h4 text-left">
                      <strong>{t("plans.name.free")}</strong>
                    </h2>

                    <p className="p--sm">{t("plans.free.description")}</p>
                  </div>
                </div>
              </div>

              <button
                className="btn"
                onClick={() => {
                  gtmSignupStart("free")()
                  onSelectPlan("free")
                }}
              >
                {t("common.get-started")}
              </button>
            </div>
          )}
          {shouldRenderUltimateFooter && (
            <div className="plan-columns__footer">
              <div className="col-xs-12 col-sm-8 mb-8 md:mb-0">
                <div className="plan-columns__footer-title">
                  <img
                    src={`/icons/signup/ultimate.svg`}
                    alt=""
                    role="presentation"
                  />
                  <div className="ml-2 mb-0 h4 ">
                    {t("plans.name.ultimate")}{" "}
                    <span className="plan-card__ai-badge">
                      {t("plans.ai-badge")}
                    </span>
                  </div>
                </div>

                <p className="pt-2 p--sm plan-columns__footer-text">
                  {t("plans.ultimate.description")}
                </p>
              </div>

              <button
                className="btn"
                onClick={() => {
                  gtmSignupStart("ultimate")()
                  onSelectPlan("ultimate")
                }}
              >
                {t("common.try-free")}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default PlanColumns
